import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { Module } from '../../follow-ups/models/deviation';
import { FeaturesBaseServices } from '../../features/services/features-base.services';
import { BaseServices } from '../../kuba.services';
import { BusinessServices } from '../../businesses/services/business.services';
import { ActivatedRoute, NavigationStart, NavigationEnd, NavigationCancel, NavigationError, NavigationSkipped, Router } from '@angular/router';
import { FlexiComplianceService } from '../compliance/flexi-compliance.service';
import { Application, FeatureKey } from '../../../_models/feature';
import { FlexiEmojiEntryService } from '../services/flexi-emoji-entry.service';
import { BookingPortalService } from '../booking-portal/booking-portal.service';
import { isNavigableFlexiTab } from '../../../shared/ecpl-tab-navigation/tab-navigation.rules';

/**
 * Landing tab for Flexi. Labelled "General" (was "Overview") — `id` 61, the
 * "HSE-advisor" linkText and the FLEXI_OVERVIEW_TAB translation key are
 * deliberately unchanged: the id is the feature-tree contract, the linkText is
 * the route, and the key is referenced from the per-business feature JSON in
 * Database/add-flexi-subtabs-as-subfeatures.sql, so renaming any of them would
 * orphan existing rows or break deep links. Only the label moves — the
 * displayed text lives in the i18n files under FLEXI_OVERVIEW_TAB.
 */
const OVERVIEW_TAB = {
    "id": 61,
    "name": "General",
    "checked": true,
    "linkText": "HSE-advisor",
    "access": true,
    "navigationRoles": "P",
    "translationKey": "FLEXI_OVERVIEW_TAB",
    "key": "",
    "IsFeature": "FN",
    "IsDefault": 1
};

const DOCUMENTS_TAB = {
    "id": 143,
    "name": "Documents",
    "checked": true,
    "linkText": "document/list",
    "access": true,
    "navigationRoles": "P",
    "translationKey": "DOCUMENTS",
    "key": "",
    "IsFeature": "FN",
    "IsDefault": 0
};

/**
 * Risk Analysis, reached inside Flexi.
 *
 * Same feature id (138 — FeatureKey.RISK_ANALYSIS) and same components as the
 * Risk Analysis under Follow-ups: this is one module surfaced in two places,
 * not a second one, so the id is deliberately shared the way Documents (143)
 * is shared across every module. Consequence to know about: the dashboard's
 * card gate (DashboardComponent.flattenFind) flattens parents and children
 * together and takes the FIRST id match, so it answers from whichever 138 the
 * feature JSON lists first — today the Follow-ups (15) child, since 15 sorts
 * ahead of Flexi (60). The Flexi tab itself is gated correctly regardless,
 * because rebuildTabs only ever reads the Flexi node's own children.
 *
 * IK, dinHMS and Agil only — see RISK_ANALYSIS_APPLICATIONS.
 */
const RISK_ANALYSIS_TAB = {
    "id": 138,
    "name": "Risk Analysis",
    "checked": true,
    "linkText": "risk-analysis",
    "access": true,
    "navigationRoles": "P",
    "translationKey": "RISK_ANALYSIS",
    "key": "",
    "IsFeature": "FN",
    "IsDefault": 1
};

/**
 * Always-on customer tab — Appendix C static link grid. No backend dependency,
 * no role gating: every Flexi user sees it.
 */
const ADDITIONAL_RESOURCES_TAB = {
    "id": 212,
    "name": "Additional Resources",
    "checked": true,
    "linkText": "additional-resources",
    "access": true,
    "navigationRoles": "P",
    "translationKey": "FLEXI_ONBOARDING.ADDITIONAL_RESOURCES.TAB",
    "key": "",
    "IsFeature": "FN",
    "IsDefault": 1
};

/**
 * BHT-samsvar / OHS Compliance Review tab.
 *
 * Gated on the sub-feature flag in Business.Features JSON (child 213 of Flexi).
 * The BE seeds an assessment on first CSM GET so the CSM can start rating
 * immediately. The customer view shows "locked" until the CSM clicks Complete.
 */
const COMPLIANCE_TAB = {
    "id": 213,
    "name": "OHS Compliance Review",
    "checked": true,
    "linkText": "compliance",
    "access": true,
    "navigationRoles": "P",
    "translationKey": "FLEXI_COMPLIANCE.TAB",
    "key": "",
    "IsFeature": "FN",
    "IsDefault": 1
};

/**
 * Vernerunder (safety rounds) tab.
 *
 * Gated like the compliance tab: child 230 of Flexi in Business.Features. The
 * list is readable by everyone in the business; starting, grading and
 * completing a round is refused by the backend to anyone but an Admin, one of
 * the business's HSE advisors or its OHS responsible, and the components hide
 * those controls to match.
 */
const SAFETY_ROUNDS_TAB = {
    "id": FeatureKey.FLEXI_SAFETY_ROUNDS,
    "name": "Safety Rounds",
    "checked": true,
    "linkText": "safety-rounds",
    "access": true,
    "navigationRoles": "P",
    "translationKey": "FLEXI_SAFETY_ROUNDS.TAB",
    "key": "",
    "IsFeature": "FN",
    "IsDefault": 1
};

/**
 * Exposure Register tab (id 231): the business's exposure assessments and
 * whether health checks are needed.
 *
 * Gated like the safety-rounds tab: child 231 of Flexi in Business.Features,
 * and until a script registers that child, through this fallback catalogue
 * only. Everyone in the business may read the assessments; starting, editing
 * and completing one is refused by the backend to anyone but an Admin, one of
 * the business's HSE advisors or its OHS responsible, and the pages hide those
 * controls to match.
 */
const EXPOSURE_REGISTER_TAB = {
    "id": FeatureKey.FLEXI_EXPOSURE_REGISTER,
    "name": "Exposure Register",
    "checked": true,
    "linkText": "exposure-register",
    "access": true,
    "navigationRoles": "P",
    "translationKey": "FLEXI_EXPOSURE.TAB",
    "key": "",
    "IsFeature": "FN",
    "IsDefault": 1
};

/**
 * Flat list of every action point on the business's compliance assessment
 * (loose and line-item-attached, line-item ones enriched with originating
 * question). Tab is appended after Flexi mounts only when the probe call
 * returns ≥ 1 row — keeps the nav clean for businesses that have nothing
 * captured yet.
 *
 * Role-gated as well as data-gated: action points name a responsible person
 * and record what they have not done, so the BE refuses the list to anyone
 * outside Editor / Portal / Admin, the assigned HSE advisor, and the user named
 * as the business's OHS responsible for action points. That 403 is what hides
 * the tab from a rank-and-file employee — see tryAppendActionPointsTab.
 */
const ACTION_POINTS_TAB = {
    "id": 216,
    "name": "Action Points",
    "checked": true,
    "linkText": "action-points",
    "access": true,
    "navigationRoles": "P",
    "translationKey": "FLEXI_COMPLIANCE.ACTION_POINTS_TAB.TAB",
    "key": "",
    "IsFeature": "FN",
    "IsDefault": 1
};

/**
 * Admin mode's only Flexi tab: the HSE advisors of every Flexi business in the
 * application (id 218). It is application-wide, so it never comes from a
 * business's feature JSON — see isAdminMode — and rebuildTabs drops it from a
 * business catalogue should it ever appear there.
 */
const SETTINGS_TAB = {
    "id": FeatureKey.FLEXI_SETTINGS,
    "name": "Settings",
    "checked": true,
    "linkText": "settings",
    "access": true,
    "navigationRoles": "A",
    "translationKey": "FLEXI_SETTINGS.TAB",
    "key": "",
    "IsFeature": "FN",
    "IsDefault": 1
};

/**
 * Booking portal (id 217, linkText "booking-portal") has no constant here, on
 * purpose.
 *
 * It exists on Agil and dinHMS only, and it reaches the tab strip solely
 * through the resolved per-business catalogue — registered as a Flexi child
 * for application 9 by
 * Database/Flexi/20260917_1430_0000_ReplaceAgilFlexiQuoteTabsWithBookingPortal.sql
 * and for application 3 by
 * Database/Flexi/20261006_2037_0000_AddBookingPortalToDinHms.sql, which both
 * uncheck Additional Modules (214) and Additional Services (215), since this
 * tab replaces them. Agil's orders go to Agil; dinHMS's go to Kuba sales.
 *
 * Adding it to DEFAULT_FLEXI_TABS would put it in front of every application
 * whose feature JSON has no Flexi children, because rebuildTabs keeps a
 * fallback tab on `checked || IsDefault` and it would carry checked: true.
 * A tab that places orders with a provider must not appear that way.
 * The route stays registered regardless; the backend refuses an order from any
 * application with no BHT recipients configured, so a hand-typed URL gets a
 * form that cannot submit rather than one that reaches the wrong company.
 */

/**
 * Fallback tab catalogue, in display order.
 *
 * The real catalogue lives in Application.Features / Application.FeaturesTemplate
 * as the `children` array of Flexi (60), which is what makes the tabs
 * selectable per business from the admin feature tree. This array is only used
 * when the resolved Flexi node has no children — i.e. an application whose
 * feature JSON has not been migrated yet — so Flexi never comes up with an
 * empty tab bar. Every entry here carries IsDefault: 1 so the fallback keeps
 * the pre-feature-flag behaviour of showing everything.
 */
/**
 * Fallback tab order, used until the per-business catalogue resolves (and for
 * businesses whose feature JSON has no Flexi children yet).
 *
 * Leads with the work: General, then the compliance review, then the
 * commitments it produced, then the documents behind them. The request-a-quote
 * and reference tabs follow, since they are browsed rather than worked.
 *
 * An admin can reorder this per application by reordering the Flexi node's
 * `children` array — see Database/add-flexi-subtabs-as-subfeatures.sql, which
 * holds the same order canonically.
 */
/**
 * Applications whose Flexi carries the Risk Analysis tab: IK, dinHMS and
 * Agil. Every other application's Flexi is the eight-tab set.
 *
 * This gates the FALLBACK catalogue only. The authority is
 * Application.Features — Database/add-flexi-subtabs-as-subfeatures.sql splices
 * child 138 in for these applications and actively retires it everywhere
 * else, so a resolved catalogue is already correct and is used verbatim (an
 * admin who unticks the tab must stay unticked). This list exists because
 * DEFAULT_FLEXI_TABS is used when the Flexi node has NO children at all, where
 * there is no per-application answer to read. Keep it in step with
 * @RiskAnalysisAppIds in that script.
 */
const RISK_ANALYSIS_APPLICATIONS: number[] = [Application.IK, Application.DIN_HMS, Application.AGIL];

/**
 * Tabs whose own template carries the redesigned card layout, keyed by the
 * `linkText` that forms their route segment.
 *
 * The shell drops its legacy white panel for these and hands them the plain
 * canvas instead; every other tab was built to sit inside that panel and
 * keeps it. Matching is on a SINGLE segment after /Flexi, so the compliance
 * LIST (/Flexi/compliance) flattens while the rating and template views
 * beneath it (/Flexi/compliance/42) keep the panel their markup expects.
 *
 * Add a linkText here as each remaining tab is redesigned.
 */
const FLAT_TABS: string[] = ['HSE-advisor', 'compliance', 'settings'];

/**
 * Tabs that bring their own cards on EVERY page beneath them, not just on the
 * tab route itself: the safety-round page (/Flexi/safety-rounds/42), the
 * exposure-assessment wizard (/Flexi/exposure-register/7) and the template
 * libraries and editors under Settings are card layouts too.
 */
const FLAT_TAB_TREES: string[] = ['safety-rounds', 'deviations', 'exposure-register', 'settings'];

const DEFAULT_FLEXI_TABS: any[] = [
    OVERVIEW_TAB,
    COMPLIANCE_TAB,
    SAFETY_ROUNDS_TAB,
    EXPOSURE_REGISTER_TAB,
    ACTION_POINTS_TAB,
    DOCUMENTS_TAB,
    RISK_ANALYSIS_TAB,
    ADDITIONAL_RESOURCES_TAB
];

@Component({
    templateUrl: './flexi.component.html',
    styleUrls: ['./flexi.component.scss'],
    standalone: false
})
export class FlexiComponent implements OnInit, OnDestroy {
    fId: number;
    enabledAdditionalFeatures: any;
    isMedi3: boolean = false;

    /** Business the tabs are showing, rendered beside the module title. */
    businessName = '';

    /** True while the active tab brings its own cards — see FLAT_TABS. */
    isFlatTab = false;

    /**
     * Admin mode, outside any business: Flexi is application-wide there and
     * shows only the Settings tab. roleId reads '3' inside R-mode, so an admin
     * working in a business still gets that business's tabs.
     */
    readonly isAdminMode = BaseServices.roleId === '1';

    private apChangesSub?: Subscription;
    private routerSub?: Subscription;
    private businessRequests = new Subscription();
    private actionPointsProbe?: Subscription;
    private loadedBusinessId: number | null = null;
    private openingFirstTab = false;

    private get businessId(): number {
        const routedId = Number(this.activatedRoute.pathFromRoot?.find(
            route => route.routeConfig?.path === 'business/:id'
        )?.snapshot.paramMap.get('id'));
        return routedId > 0 ? routedId : BaseServices.BusinessId;
    }

  /**
   * Tab catalogue resolved from the Flexi node's `children` in
   * Business.Features. Empty while loading or unavailable. Null selects the
   * fallback only after a successful lookup of a legacy catalogue without
   * Flexi children.
   */
    private configuredTabs: any[] | null = null;

    /** Set once the action-points probe returns at least one row. */
    private hasActionPoints = false;

    /**
     * Whether the current user may place a BHT order. Starts false so the
     * Booking portal tab is hidden until the probe says otherwise — an Editor
     * must never see a tab whose submit would refuse them.
     */
    private canOrderBht = false;
    safetyRoundNavigating = false;

    constructor(
        private businessServices: BusinessServices,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private flexiComplianceService: FlexiComplianceService,
        private bookingPortalService: BookingPortalService,
        private emojiEntry: FlexiEmojiEntryService
    ) {
        this.fId = Module.FLEXI;
        this.businessName = BaseServices.BusinessName;
        // Which surface the shell hands the active tab. Evaluated now for a
        // deep link, and again on every navigation between tabs.
        this.syncFlatTab(this.router.url);
        this.routerSub = this.router.events
            .subscribe(event => {
                if (event instanceof NavigationStart) {
                    const path = (url: string) => url.split(/[?#]/)[0];
                    this.safetyRoundNavigating = path(event.url) !== path(this.router.url)
                        && [event.url, this.router.url].some(url => /\/(?:safety-rounds|safety-round-templates)(?:[\/;?#]|$)/.test(url));
                }
                if (event instanceof NavigationEnd || event instanceof NavigationCancel
                    || event instanceof NavigationError || event instanceof NavigationSkipped) {
                    this.safetyRoundNavigating = false;
                }
                if (!(event instanceof NavigationEnd)) return;
                this.syncFlatTab(event.urlAfterRedirects);
                if (!this.isAdminMode && this.loadedBusinessId !== null
                    && this.loadedBusinessId !== this.businessId) {
                    this.loadBusinessNavigation();
                }
            });
        // Arm the one-time status-emoji entry animation. Here, in the
        // constructor, is the moment the router activates /Flexi and before any
        // child tab exists — the first emoji to render consumes it. See
        // FlexiEmojiEntryService.
        this.emojiEntry.arm();

        // Admin mode has no business to probe, and the admin's own business
        // must not decide the tabs, so none of the per-business work below runs.
        if (this.isAdminMode) {
            this.businessName = '';
            this.enabledAdditionalFeatures = [SETTINGS_TAB];
            return;
        }

        // Wait for this business's feature selections before offering tab links.
        this.enabledAdditionalFeatures = [];

        // Re-probe whenever the AP list could have changed. Without this,
        // creating the very first AP from compliance-rating wouldn't surface
        // the tab until a full page refresh.
        this.apChangesSub = this.flexiComplianceService.actionPointsChanges
            .subscribe(() => this.tryAppendActionPointsTab());
    }

    ngOnDestroy(): void {
        this.apChangesSub?.unsubscribe();
        this.routerSub?.unsubscribe();
        this.businessRequests.unsubscribe();
        this.actionPointsProbe?.unsubscribe();
    }

    /**
     * Decide whether the active tab wants the plain canvas or the legacy panel.
     *
     * Matrix parameters ride on the segment (`compliance;Id=42`), so each
     * segment is cut at the first `;` before it is compared.
     */
    private syncFlatTab(url: string): void {
        const path = (url || '').split('?')[0].split('#')[0];
        const segments = path.split('/').filter(segment => segment.length > 0);
        const flexiAt = segments.findIndex(
            segment => segment.split(';')[0].toLowerCase() === 'flexi');
        const child = flexiAt >= 0 ? segments.slice(flexiAt + 1) : [];
        const first = child.length ? child[0].split(';')[0] : '';
        this.isFlatTab =
            (child.length === 1 && FLAT_TABS.indexOf(first) !== -1) ||
            FLAT_TAB_TREES.indexOf(first) !== -1;
    }

    ngOnInit() {
        if (BaseServices.ApplicationId == 5) {
            this.isMedi3 = true;
        }
        if (this.isAdminMode) {
            this.openFirstTab();
            return;
        }
        this.loadBusinessNavigation();
    }

    private loadBusinessNavigation(): void {
        this.businessRequests.unsubscribe();
        this.businessRequests = new Subscription();
        this.actionPointsProbe?.unsubscribe();
        this.loadedBusinessId = this.businessId;
        this.businessName = '';
        this.configuredTabs = [];
        this.hasActionPoints = false;
        this.canOrderBht = false;
        this.rebuildTabs();

        this.businessRequests.add(this.businessServices.getById(this.businessId).subscribe({
            next: result => {
                const featureBase = {
                    ...BaseServices.getUserFeatures(),
                    Features: result.Features,
                    Type: 'business',
                    Details: result
                };
                const flexi = FeaturesBaseServices.getFeatureDetails(featureBase)
                    .find(feature => feature.id === FeatureKey.FLEXI);
                this.businessName = result.CompanyName || '';
                if (flexi && flexi.checked !== false && flexi.access !== false) {
                    sessionStorage.setItem('workingModule', JSON.stringify(flexi));
                    // Preserve legacy catalogues without children, but never override explicit selections.
                    this.configuredTabs = flexi.children?.length ? flexi.children : null;
                }
                this.rebuildTabs();
                this.openFirstTab();
            },
            // A failed lookup cannot establish which tabs this business may use.
            error: () => { this.configuredTabs = []; this.rebuildTabs(); }
        }));
        this.tryAppendActionPointsTab();
        this.businessRequests.add(this.bookingPortalService.canOrder(this.businessId).subscribe({
            next: canOrder => { this.canOrderBht = canOrder; this.rebuildTabs(); this.openFirstTab(); },
            error: () => { this.canOrderBht = false; this.rebuildTabs(); }
        }));
    }

  /**
   * Compose the visible tab list from the catalogue.
   *
   * Three gates are applied, in this order:
   *  1. The admin feature flag — `checked` on the sub-module in
   *     Business.Features. Fallback tabs carry IsDefault: 1 so they survive
   *     this gate, preserving the pre-feature-flag behaviour. The hover
   *     sub-navigation applies the same gate (`isNavigableFlexiTab`).
   *  2. The application scope — Risk Analysis exists on IK, dinHMS and Agil only.
   *     Applied to the fallback catalogue alone; a resolved one already
   *     encodes the scope per application.
   *  3. The pre-existing data probes — Responsibilities and Duties needs
   *     either a privileged role or published content, and Action Points
   *     needs at least one captured row. An admin-enabled tab with no data
   *     behind it still stays hidden.
   *
   * Order comes from the catalogue itself, so admins control tab order by
   * ordering the `children` array.
   */
    private rebuildTabs(): void {
        const usingFallback = this.configuredTabs === null;
        const catalogue: any[] = [...(this.configuredTabs ?? DEFAULT_FLEXI_TABS)];
        this.enabledAdditionalFeatures = catalogue.filter(tab => {
            // Settings is application-wide and Admin-mode only; a business
            // catalogue must never surface it.
            if (!isNavigableFlexiTab(tab) || tab.id === FeatureKey.FLEXI_SETTINGS
                || tab.id === FeatureKey.DEVIATIONS) {
                return false;
            }
            // Risk Analysis is IK/dinHMS/Agil only. A resolved catalogue already
            // encodes that per application, so this gate applies to the
            // fallback alone — see RISK_ANALYSIS_APPLICATIONS.
            if (usingFallback && tab.id === FeatureKey.RISK_ANALYSIS) {
                return ( RISK_ANALYSIS_APPLICATIONS.indexOf(BaseServices.ApplicationId) !== -1
        );
            }
            if (tab.id === FeatureKey.FLEXI_ACTION_POINTS) {
                return this.hasActionPoints;
            }
            // Booking portal places paid orders, so it is role-gated as well as
            // feature-gated: administrator, the business's HSE adviser, or its
            // OHS responsible. The backend answers; an Editor on an Agil or dinHMS
            // business sees no tab.
            if (tab.id === FeatureKey.FLEXI_BOOKING_PORTAL) {
                return this.canOrderBht;
            }
            return true;
        }).sort((left, right) => Number(left.id === FeatureKey.FLEXI_BOOKING_PORTAL)
            - Number(right.id === FeatureKey.FLEXI_BOOKING_PORTAL));
    }

  /**
   * Auto-open the first tab only when we landed on the bare /Flexi route.
   * When Flexi is opened deep-linked to a specific child (e.g. the portal
   * "Open Flexi" → /Flexi/compliance), keep the user on that child instead
   * of bouncing them to the first tab.
   */
    private openFirstTab(): void {
        const segments = this.router.url.split(/[?#]/)[0].split('/').filter(Boolean)
            .map(segment => segment.split(';')[0]);
        const flexiAt = segments.indexOf('Flexi');
        const alreadyOnChild = flexiAt < 0 || segments.length > flexiAt + 1;
        if (!alreadyOnChild && !this.openingFirstTab && this.enabledAdditionalFeatures?.length) {
            this.openingFirstTab = true;
            this.router.navigate([this.enabledAdditionalFeatures[0].linkText, { Id: this.businessId }],
                { relativeTo: this.activatedRoute }).then(
                    () => { this.openingFirstTab = false; },
                    () => { this.openingFirstTab = false; }
                );
        }
    }

  /**
   * Probe the compliance action-points endpoint and append the tab when
   * the business has at least one captured.
   *
   * One request answers both gates, which is why the authorization decision
   * rides on the error branch here rather than on a separate access call as
   * it does for the dashboard card. The list is needed anyway to know whether
   * the business has any action points, and the endpoint is guarded by
   * `flexiCompliance:actionPoints:view`, so:
   *   • 200 + rows      → the user may see them AND there are some → show tab
   *   • 200 + no rows   → allowed, but nothing captured yet        → no tab
   *   • 403             → not allowed to see action points         → no tab
   *   • 404 / other     → no assessment or transient failure      → no tab
   */
    private tryAppendActionPointsTab(): void {
        this.actionPointsProbe?.unsubscribe();
        this.actionPointsProbe = this.flexiComplianceService.listActionPoints(this.businessId).subscribe({
            next: rows => {
                // One-way latch, as before: once the tab is up it stays up for
                // the rest of the session. Deleting the last action point must
                // not yank the tab out from under a user who is standing on it.
                if (rows && rows.length > 0 && !this.hasActionPoints) {
                    this.hasActionPoints = true;
                    this.rebuildTabs();
                    this.openFirstTab();
                }
            },
            // 403 (not permitted to see action points), 404 (no assessment)
            // and transient failures all land here and leave the tab hidden.
            error: () => { /* No access / no assessment / error -> hide tab. */ }
        });
    }
}
